import { observer } from "mobx-react-lite";

import Messages from "@/components/Playground/Messages";
import {
  IPlayground,
  PlaygroundStoreProvider,
  usePlayground,
} from "@/components/Playground/playground-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RewindTemplateDialogProps {
  output: IPlayground;
  isOpen: boolean;
  hasDifferentState: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmRewind: () => void;
}

const TemplatePreview = observer(() => {
  const previewPlayground = usePlayground();

  return (
    <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <Messages
        messages={previewPlayground.messages}
        onAddMessage={previewPlayground.addEmptyMessage}
      />
    </div>
  );
});

const RewindTemplateDialog = ({
  output,
  isOpen,
  hasDifferentState,
  onOpenChange,
  onConfirmRewind,
}: RewindTemplateDialogProps) => {
  const handleConfirmRewind = () => {
    onConfirmRewind();
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[82vh] w-[calc(100vw-2rem)] max-w-[1100px] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-gray-200 bg-gray-50/70 px-5 py-4">
          <DialogTitle>Confirm rewind</DialogTitle>
          <DialogDescription>
            Review the read-only prompt template and playground state that produced this output.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col bg-gray-50/40 p-4">
          <PlaygroundStoreProvider
            value={{
              prompt_blueprint: output.rewindBlueprint,
              input_variable_sets: output.input_variable_sets,
              readonly: true,
            }}
          >
            <TemplatePreview />
          </PlaygroundStoreProvider>
        </div>

        <DialogFooter className="border-t border-gray-200 bg-white px-5 py-3">
          <div className="flex w-full items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {hasDifferentState
                ? "Rewinding will replace your current playground configuration with this past state."
                : "Your playground already matches this output’s template state."}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={handleConfirmRewind} disabled={!hasDifferentState}>
                Rewind
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default observer(RewindTemplateDialog);
